"use client";

import { useRouter, usePathname } from "next/navigation";
import { useEffect, useState, type PropsWithChildren, type ReactNode } from "react";
import { useAuth } from "@/app/auth/AuthSessionProvider";

const shouldLogDebug = process.env.NODE_ENV !== "production";
const COMPLETE_PROFILE_PATH = "/account/complete-profile/";

/** Strips a trailing slash so `/account/complete-profile/` matches the static export route. */
export function normalizeConsumerPath(pathname: string | null | undefined): string {
  if (!pathname) return "";
  if (pathname.length > 1 && pathname.endsWith("/")) return pathname.slice(0, -1);
  return pathname;
}

/** True for the name/DNI completion page, with or without a trailing slash. */
export function isCompleteProfilePath(pathname: string | null | undefined): boolean {
  return normalizeConsumerPath(pathname) === "/account/complete-profile";
}

/**
 * Redirects to /account/complete-profile/ when the Users profile is incomplete.
 * Refreshes the opaque session first: login snapshots `profileComplete`, so a stale
 * false would otherwise trap the user on the complete-profile success card.
 */
export function ProfileCompleteGate({ children }: PropsWithChildren): ReactNode {
  const { user, isLoading, isAuthenticated, refreshSession } = useAuth();
  const isHydrated = !isLoading;
  const profileComplete = user?.profileComplete;
  const router = useRouter();
  const pathname = usePathname();
  const isCompleteProfileRoute = isCompleteProfilePath(pathname);
  const isAuthRoute = normalizeConsumerPath(pathname).startsWith("/auth");
  const shouldSkipGate = isCompleteProfileRoute || isAuthRoute;
  const [sessionVerifiedComplete, setSessionVerifiedComplete] = useState(false);
  const [confirmedIncomplete, setConfirmedIncomplete] = useState(false);

  useEffect(() => {
    setSessionVerifiedComplete(false);
    setConfirmedIncomplete(false);
  }, [user?.id]);

  useEffect(() => {
    if (!isHydrated || !isAuthenticated || shouldSkipGate) {
      return;
    }
    if (profileComplete === true || sessionVerifiedComplete) {
      setSessionVerifiedComplete(true);
      return;
    }
    if (confirmedIncomplete) {
      router.replace(COMPLETE_PROFILE_PATH);
      return;
    }

    let cancelled = false;
    if (shouldLogDebug) {
      console.log("[ProfileCompleteGate] Refreshing session before incomplete-profile redirect");
    }
    void (async () => {
      const synced = await refreshSession();
      if (cancelled) return;
      if (synced?.profileComplete === true) {
        if (shouldLogDebug) {
          console.log("[ProfileCompleteGate] Session was stale; profile is complete after refresh");
        }
        setSessionVerifiedComplete(true);
        return;
      }
      if (shouldLogDebug) {
        console.log("[ProfileCompleteGate] Profile incomplete after refresh, redirecting to complete-profile");
      }
      setConfirmedIncomplete(true);
      router.replace(COMPLETE_PROFILE_PATH);
    })();
    return () => {
      cancelled = true;
    };
  }, [
    isHydrated,
    isAuthenticated,
    shouldSkipGate,
    profileComplete,
    sessionVerifiedComplete,
    confirmedIncomplete,
    refreshSession,
    router,
  ]);

  if (!isHydrated) {
    return null;
  }

  if (!isAuthenticated || !user) {
    return <>{children}</>;
  }

  if (shouldSkipGate) {
    return <>{children}</>;
  }

  if (profileComplete === true || sessionVerifiedComplete) {
    return <>{children}</>;
  }

  if (!confirmedIncomplete) {
    return (
      <div className="gruv-page" style={{ textAlign: "center", padding: "4rem 1rem" }}>
        <p>Verificando perfil...</p>
      </div>
    );
  }

  return (
    <div className="gruv-page" style={{ textAlign: "center", padding: "4rem 1rem" }}>
      <p>Redirigiendo...</p>
    </div>
  );
}
